import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  Filters,
  Input,
  LoadingState,
  Pagination,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createSystemApi } from "../api";
import {
  accessStatusTone,
  configStatusTone,
  flagStatusTone,
  formatLabel,
  maintenanceStatusTone,
} from "../labels";
import { useSystemQueue } from "../useSystemQueue";
import type {
  AccessAssignmentDto,
  AuditLogDto,
  ConfigurationDto,
  FeatureFlagDto,
  IntegrationEventDto,
  MaintenanceDto,
} from "../types";

const HUB_QUEUES = [
  {
    to: "/app/system/maintenance",
    kind: "Live status",
    title: "Maintenance modes",
    body: "Active and scheduled maintenance windows by scope. Distinct from static configuration.",
  },
  {
    to: "/app/system/configurations",
    kind: "Configuration",
    title: "Platform configurations",
    body: "Governed configuration records (type, scope, status). Values are summarized, never expanded.",
  },
  {
    to: "/app/system/feature-flags",
    kind: "Configuration",
    title: "Feature flags",
    body: "Existing flag records and rollout metadata. No inventing flags or editors.",
  },
  {
    to: "/app/system/access",
    kind: "Access",
    title: "Access assignments",
    body: "Employee access assignments with scopes and grants on detail. No secret exposure.",
  },
  {
    to: "/app/system/audit-logs",
    kind: "Investigation",
    title: "Audit logs",
    body: "Read-only investigation history. Prior/resulting state and security context values withheld.",
  },
  {
    to: "/app/system/integration-events",
    kind: "Operations",
    title: "Integration events",
    body: "Operational integration event history (status, attempts, references). Payloads not expanded.",
  },
] as const;

/** The "Operations and governance" tab of the System page. */
export function SystemOperationsPanel() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createSystemApi(client), [client]);

  const [summary, setSummary] = useState<{
    activeMaintenance: number | null;
    activeFlags: number | null;
    activeConfigs: number | null;
    recentAudit: number | null;
    failedIntegrations: number | null;
  } | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    // One forbidden or failing list must not blank the other four counts, so
    // each is settled on its own and the failures are reported together.
    const [maint, flags, configs, audit, integrations] = await Promise.allSettled([
      api.listMaintenance({ status: "active", limit: 1 }),
      api.listFeatureFlags({ status: "active", limit: 1 }),
      api.listConfigurations({ status: "active", limit: 1 }),
      api.listAuditLogs({ limit: 1 }),
      api.listIntegrationEvents({ status: "failed", limit: 1 }),
    ]);
    const failures: string[] = [];
    const note = (name: string, r: PromiseSettledResult<unknown>) => {
      if (r.status === "rejected") failures.push(`${name}: ${apiErrorMessage(r.reason)}`);
    };
    note("Maintenance", maint);
    note("Feature flags", flags);
    note("Configurations", configs);
    note("Audit log", audit);
    note("Integration events", integrations);

    setSummary({
      activeMaintenance:
        maint.status === "fulfilled" ? countFromMeta(maint.value.meta?.status_counts, "active") : null,
      activeFlags:
        flags.status === "fulfilled" ? countFromMeta(flags.value.meta?.status_counts, "active") : null,
      activeConfigs:
        configs.status === "fulfilled" ? countFromMeta(configs.value.meta?.status_counts, "active") : null,
      recentAudit:
        audit.status === "fulfilled"
          ? audit.value.meta?.status_counts == null
            ? audit.value.items.length
            : Object.values(audit.value.meta.status_counts).reduce((x, y) => x + y, 0)
          : null,
      failedIntegrations:
        integrations.status === "fulfilled"
          ? countFromMeta(integrations.value.meta?.status_counts, "failed")
          : null,
    });
    setSummaryError(failures.length > 0 ? failures.join(" · ") : null);
    setSummaryLoading(false);
  }, [api]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Operations and governance"
        description="Maintenance windows, configuration, feature flags, staff access and the audit trail. Secrets, tokens, raw environment values and opaque JSON values are never shown. Lifecycle actions require confirmation and a reason."
      />

      {summaryLoading ? (
        <LoadingState message="Loading operational summary…" />
      ) : (
        <Stack gap={12}>
          {summaryError ? (
            <Alert tone="warning" title="Some counts are unavailable">
              {summaryError}. The other counts below are unaffected.
              <Button variant="ghost" size="sm" onClick={() => void loadSummary()}>
                Retry
              </Button>
            </Alert>
          ) : null}
          {summary ? (
            <Flex gap={12} wrap>
              <SummaryChip label="Active maintenance" value={summary.activeMaintenance} kind="Live status" />
              <SummaryChip label="Active feature flags" value={summary.activeFlags} kind="Configuration" />
              <SummaryChip label="Active configurations" value={summary.activeConfigs} kind="Configuration" />
              <SummaryChip label="Audit trail reachable" value={summary.recentAudit} kind="Investigation" />
              <SummaryChip label="Failed integration events" value={summary.failedIntegrations} kind="Operations" />
            </Flex>
          ) : null}
        </Stack>
      )}

      <Flex gap={16} wrap>
        {HUB_QUEUES.map((queue) => (
          <Card key={queue.to} style={{ minWidth: 260, flex: "1 1 260px" }}>
            <Stack gap={8}>
              <Text role="small" color="secondary">
                {queue.kind}
              </Text>
              <Text role="title">{queue.title}</Text>
              <Text role="small" color="secondary" as="p">
                {queue.body}
              </Text>
              <Button
                variant="secondary"
                onClick={() => navigate(queue.to)}
              >
                Open
              </Button>
            </Stack>
          </Card>
        ))}
      </Flex>

      <Text role="small" color="secondary" as="p">
        Break-glass sessions and temporary delegations remain available on the
        System API; this wave surfaces access assignments, configurations, flags,
        maintenance, audit logs, and integration events.
      </Text>
    </Stack>
  );
}

function SummaryChip({
  label,
  value,
  kind,
}: {
  label: string;
  value: number | null;
  kind: string;
}) {
  return (
    <Card style={{ minWidth: 160, flex: "1 1 160px" }}>
      <Stack gap={4}>
        <Text role="small" color="secondary">
          {kind}
        </Text>
        <Text role="title">{value == null ? "—" : String(value)}</Text>
        <Text role="small">{label}</Text>
      </Stack>
    </Card>
  );
}

function countFromMeta(
  counts: Record<string, number> | undefined,
  key: string,
): number | null {
  if (!counts) return null;
  return counts[key] ?? 0;
}

function QueueShell({
  title,
  description,
  breadcrumbs,
  filters,
  loading,
  error,
  empty,
  children,
  page,
  nextCursor,
  onRetry,
  onPrev,
  onNext,
  onClear,
}: {
  title: string;
  description: string;
  breadcrumbs: { label: string; to?: string }[];
  filters: ReactNode;
  loading: boolean;
  error: string | null;
  empty: boolean;
  children: ReactNode;
  page: number;
  nextCursor: string | null;
  onRetry: () => void;
  onPrev: () => void;
  onNext: () => void;
  onClear: () => void;
}) {
  usePageBreadcrumbs(breadcrumbs);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={title}
        description={description}
        actions={
          <Flex gap={8} wrap>
            <Button variant="secondary" onClick={onClear}>
              Clear filters
            </Button>
            <Button variant="secondary" onClick={onRetry}>
              Refresh
            </Button>
          </Flex>
        }
      />
      <Filters>{filters}</Filters>
      {loading ? <LoadingState message="Loading…" /> : null}
      {error ? (
        <Alert tone="danger" title="Load failed">
          {error}
        </Alert>
      ) : null}
      {!loading && !error && empty ? (
        <EmptyState
          title="No records"
          description="No rows for the current filters, or the API returned an empty page."
          reason="none"
        />
      ) : null}
      {!loading && !error && !empty ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Page {page}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page) onNext();
                  if (nextPage < page) onPrev();
                }}
              />
            }
          />
          {children}
        </Stack>
      ) : null}
    </Stack>
  );
}

export function MaintenanceQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createSystemApi(client), [client]);
  const loadPage = useCallback(
    async ({
      cursor,
      status,
      sort,
      extra,
    }: {
      cursor?: string;
      status: string;
      sort: string;
      extra: Record<string, string>;
    }) => {
      const result = await api.listMaintenance({
        ...(cursor ? { cursor } : {}),
        ...(status ? { status } : {}),
        ...(sort ? { sort } : {}),
        ...(extra.scope_type ? { scope_type: extra.scope_type } : {}),
        ...(extra.owner_user_id
          ? { owner_user_id: extra.owner_user_id }
          : {}),
        limit: 25,
      });
      return { items: result.items, nextCursor: result.next_cursor ?? null };
    },
    [api],
  );
  const queue = useSystemQueue<MaintenanceDto>(loadPage);

  return (
    <QueueShell
      title="Maintenance modes"
      description="Live operational windows. Activate/end change availability for the recorded scope."
      breadcrumbs={[
        { label: "HUDDL HQ", to: "/app" },
        { label: "System", to: "/app/system" },
        { label: "Maintenance" },
      ]}
      filters={
        <>
          <Select
            label="Status"
            value={queue.status}
            onChange={(e) => queue.setStatus(e.target.value)}
            options={[
              { value: "", label: "Any" },
              ...[
                "proposed",
                "approved",
                "scheduled",
                "active",
                "ended",
                "cancelled",
              ].map((value) => ({ value, label: formatLabel(value) })),
            ]}
          />
          <Input
            label="Scope type"
            value={queue.extra.scope_type ?? ""}
            onChange={(e) => queue.setExtra("scope_type", e.target.value)}
          />
        </>
      }
      loading={queue.loading}
      error={queue.error}
      empty={queue.rows.length === 0}
      page={queue.page}
      nextCursor={queue.nextCursor}
      onRetry={() => void queue.load()}
      onPrev={queue.goPrevious}
      onNext={queue.goNext}
      onClear={queue.clearFilters}
    >
      <Table>
        <Thead>
          <Tr>
            <Th>ID</Th>
            <Th>Status</Th>
            <Th>Scope</Th>
            <Th>Starts</Th>
            <Th>Ends</Th>
          </Tr>
        </Thead>
        <Tbody>
          {queue.rows.map((row) => (
            <Tr
              key={row.id}
              onClick={() => navigate(`/app/system/maintenance/${row.id}`)}
              style={{ cursor: "pointer" }}
            >
              <Td>{row.id}</Td>
              <Td>
                <StatusChip tone={maintenanceStatusTone(row.status)}>
                  {formatLabel(row.status)}
                </StatusChip>
              </Td>
              <Td>
                {formatLabel(row.scope_type)}
                {row.scope_id ? ` · ${row.scope_id}` : ""}
              </Td>
              <Td>{row.starts_at}</Td>
              <Td>{row.ends_at ?? "—"}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </QueueShell>
  );
}

export function ConfigurationsQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createSystemApi(client), [client]);
  const loadPage = useCallback(
    async ({
      cursor,
      status,
      sort,
      extra,
    }: {
      cursor?: string;
      status: string;
      sort: string;
      extra: Record<string, string>;
    }) => {
      const result = await api.listConfigurations({
        ...(cursor ? { cursor } : {}),
        ...(status ? { status } : {}),
        ...(sort ? { sort } : {}),
        ...(extra.configuration_type
          ? { configuration_type: extra.configuration_type }
          : {}),
        ...(extra.scope_type ? { scope_type: extra.scope_type } : {}),
        limit: 25,
      });
      return { items: result.items, nextCursor: result.next_cursor ?? null };
    },
    [api],
  );
  const queue = useSystemQueue<ConfigurationDto>(loadPage);

  return (
    <QueueShell
      title="Platform configurations"
      description="Static governed configuration. Values are summarized by key names only — never shown raw."
      breadcrumbs={[
        { label: "HUDDL HQ", to: "/app" },
        { label: "System", to: "/app/system" },
        { label: "Configurations" },
      ]}
      filters={
        <>
          <Select
            label="Status"
            value={queue.status}
            onChange={(e) => queue.setStatus(e.target.value)}
            options={[
              { value: "", label: "Any" },
              ...[
                "draft",
                "approved",
                "scheduled",
                "active",
                "paused",
                "replaced",
                "retired",
              ].map((value) => ({ value, label: formatLabel(value) })),
            ]}
          />
          <Input
            label="Configuration type"
            value={queue.extra.configuration_type ?? ""}
            onChange={(e) =>
              queue.setExtra("configuration_type", e.target.value)
            }
          />
        </>
      }
      loading={queue.loading}
      error={queue.error}
      empty={queue.rows.length === 0}
      page={queue.page}
      nextCursor={queue.nextCursor}
      onRetry={() => void queue.load()}
      onPrev={queue.goPrevious}
      onNext={queue.goNext}
      onClear={queue.clearFilters}
    >
      <Table>
        <Thead>
          <Tr>
            <Th>ID</Th>
            <Th>Type</Th>
            <Th>Status</Th>
            <Th>Scope</Th>
            <Th>Effective from</Th>
          </Tr>
        </Thead>
        <Tbody>
          {queue.rows.map((row) => (
            <Tr
              key={row.id}
              onClick={() =>
                navigate(`/app/system/configurations/${row.id}`)
              }
              style={{ cursor: "pointer" }}
            >
              <Td>{row.id}</Td>
              <Td>{formatLabel(row.configuration_type)}</Td>
              <Td>
                <StatusChip tone={configStatusTone(row.status)}>
                  {formatLabel(row.status)}
                </StatusChip>
              </Td>
              <Td>
                {formatLabel(row.scope_type)}
                {row.scope_id ? ` · ${row.scope_id}` : ""}
              </Td>
              <Td>{row.effective_from}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </QueueShell>
  );
}

export function FeatureFlagsQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createSystemApi(client), [client]);
  const loadPage = useCallback(
    async ({
      cursor,
      status,
      sort,
      extra,
    }: {
      cursor?: string;
      status: string;
      sort: string;
      extra: Record<string, string>;
    }) => {
      const result = await api.listFeatureFlags({
        ...(cursor ? { cursor } : {}),
        ...(status ? { status } : {}),
        ...(sort ? { sort } : {}),
        ...(extra.rollout_type ? { rollout_type: extra.rollout_type } : {}),
        ...(extra.flag_key ? { flag_key: extra.flag_key } : {}),
        limit: 25,
      });
      return { items: result.items, nextCursor: result.next_cursor ?? null };
    },
    [api],
  );
  const queue = useSystemQueue<FeatureFlagDto>(loadPage);

  return (
    <QueueShell
      title="Feature flags"
      description="Existing governed flags only. Scope definition values are withheld."
      breadcrumbs={[
        { label: "HUDDL HQ", to: "/app" },
        { label: "System", to: "/app/system" },
        { label: "Feature flags" },
      ]}
      filters={
        <>
          <Select
            label="Status"
            value={queue.status}
            onChange={(e) => queue.setStatus(e.target.value)}
            options={[
              { value: "", label: "Any" },
              ...[
                "draft",
                "approved",
                "scheduled",
                "active",
                "paused",
                "disabled",
                "rolled_back",
                "retired",
              ].map((value) => ({ value, label: formatLabel(value) })),
            ]}
          />
          <Input
            label="Flag key"
            value={queue.extra.flag_key ?? ""}
            onChange={(e) => queue.setExtra("flag_key", e.target.value)}
          />
        </>
      }
      loading={queue.loading}
      error={queue.error}
      empty={queue.rows.length === 0}
      page={queue.page}
      nextCursor={queue.nextCursor}
      onRetry={() => void queue.load()}
      onPrev={queue.goPrevious}
      onNext={queue.goNext}
      onClear={queue.clearFilters}
    >
      <Table>
        <Thead>
          <Tr>
            <Th>Flag key</Th>
            <Th>Status</Th>
            <Th>Rollout</Th>
            <Th>Enabled at</Th>
          </Tr>
        </Thead>
        <Tbody>
          {queue.rows.map((row) => (
            <Tr
              key={row.id}
              onClick={() =>
                navigate(`/app/system/feature-flags/${row.id}`)
              }
              style={{ cursor: "pointer" }}
            >
              <Td>{row.flag_key}</Td>
              <Td>
                <StatusChip tone={flagStatusTone(row.status)}>
                  {formatLabel(row.status)}
                </StatusChip>
              </Td>
              <Td>{formatLabel(row.rollout_type)}</Td>
              <Td>{row.enabled_at ?? "—"}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </QueueShell>
  );
}

export function AccessQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createSystemApi(client), [client]);
  const loadPage = useCallback(
    async ({
      cursor,
      status,
      sort,
      extra,
    }: {
      cursor?: string;
      status: string;
      sort: string;
      extra: Record<string, string>;
    }) => {
      const result = await api.listAccessAssignments({
        ...(cursor ? { cursor } : {}),
        ...(status ? { status } : {}),
        ...(sort ? { sort } : {}),
        ...(extra.employee_role
          ? { employee_role: extra.employee_role }
          : {}),
        ...(extra.user_id ? { user_id: extra.user_id } : {}),
        limit: 25,
      });
      return { items: result.items, nextCursor: result.next_cursor ?? null };
    },
    [api],
  );
  const queue = useSystemQueue<AccessAssignmentDto>(loadPage);

  return (
    <QueueShell
      title="Access assignments"
      description="Employee role assignments. Scopes and grants appear on detail. No credential or token fields."
      breadcrumbs={[
        { label: "HUDDL HQ", to: "/app" },
        { label: "System", to: "/app/system" },
        { label: "Access" },
      ]}
      filters={
        <>
          <Select
            label="Status"
            value={queue.status}
            onChange={(e) => queue.setStatus(e.target.value)}
            options={[
              { value: "", label: "Any" },
              ...[
                "requested",
                "approved",
                "active",
                "adjusted",
                "suspended",
                "revoked",
                "expired",
              ].map((value) => ({ value, label: formatLabel(value) })),
            ]}
          />
          <Input
            label="Employee role"
            value={queue.extra.employee_role ?? ""}
            onChange={(e) => queue.setExtra("employee_role", e.target.value)}
          />
          <Input
            label="User ID"
            value={queue.extra.user_id ?? ""}
            onChange={(e) => queue.setExtra("user_id", e.target.value)}
          />
        </>
      }
      loading={queue.loading}
      error={queue.error}
      empty={queue.rows.length === 0}
      page={queue.page}
      nextCursor={queue.nextCursor}
      onRetry={() => void queue.load()}
      onPrev={queue.goPrevious}
      onNext={queue.goNext}
      onClear={queue.clearFilters}
    >
      <Table>
        <Thead>
          <Tr>
            <Th>ID</Th>
            <Th>User</Th>
            <Th>Role</Th>
            <Th>Status</Th>
            <Th>Effective from</Th>
          </Tr>
        </Thead>
        <Tbody>
          {queue.rows.map((row) => (
            <Tr
              key={row.id}
              onClick={() => navigate(`/app/system/access/${row.id}`)}
              style={{ cursor: "pointer" }}
            >
              <Td>{row.id}</Td>
              <Td>{row.user_id}</Td>
              <Td>{formatLabel(row.employee_role)}</Td>
              <Td>
                <StatusChip tone={accessStatusTone(row.status)}>
                  {formatLabel(row.status)}
                </StatusChip>
              </Td>
              <Td>{row.effective_from}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </QueueShell>
  );
}

export function AuditLogsQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createSystemApi(client), [client]);
  const loadPage = useCallback(
    async ({
      cursor,
      status,
      sort,
      extra,
    }: {
      cursor?: string;
      status: string;
      sort: string;
      extra: Record<string, string>;
    }) => {
      void status;
      const result = await api.listAuditLogs({
        ...(cursor ? { cursor } : {}),
        ...(sort ? { sort } : {}),
        ...(extra.action ? { action: extra.action } : {}),
        ...(extra.target_type ? { target_type: extra.target_type } : {}),
        ...(extra.target_id ? { target_id: extra.target_id } : {}),
        ...(extra.actor_user_id
          ? { actor_user_id: extra.actor_user_id }
          : {}),
        ...(extra.classification
          ? { classification: extra.classification }
          : {}),
        limit: 25,
      });
      return { items: result.items, nextCursor: result.next_cursor ?? null };
    },
    [api],
  );
  const queue = useSystemQueue<AuditLogDto>(loadPage, {
    sort: "-occurred_at",
  });

  return (
    <QueueShell
      title="Audit logs"
      description="Read-only investigation. State blobs and security context values are withheld."
      breadcrumbs={[
        { label: "HUDDL HQ", to: "/app" },
        { label: "System", to: "/app/system" },
        { label: "Audit logs" },
      ]}
      filters={
        <>
          <Input
            label="Action"
            value={queue.extra.action ?? ""}
            onChange={(e) => queue.setExtra("action", e.target.value)}
          />
          <Input
            label="Target type"
            value={queue.extra.target_type ?? ""}
            onChange={(e) => queue.setExtra("target_type", e.target.value)}
          />
          <Input
            label="Target ID"
            value={queue.extra.target_id ?? ""}
            onChange={(e) => queue.setExtra("target_id", e.target.value)}
          />
          <Input
            label="Actor user ID"
            value={queue.extra.actor_user_id ?? ""}
            onChange={(e) => queue.setExtra("actor_user_id", e.target.value)}
          />
        </>
      }
      loading={queue.loading}
      error={queue.error}
      empty={queue.rows.length === 0}
      page={queue.page}
      nextCursor={queue.nextCursor}
      onRetry={() => void queue.load()}
      onPrev={queue.goPrevious}
      onNext={queue.goNext}
      onClear={queue.clearFilters}
    >
      <Table>
        <Thead>
          <Tr>
            <Th>Occurred</Th>
            <Th>Action</Th>
            <Th>Target</Th>
            <Th>Actor</Th>
            <Th>Classification</Th>
          </Tr>
        </Thead>
        <Tbody>
          {queue.rows.map((row) => (
            <Tr
              key={row.id}
              onClick={() => navigate(`/app/system/audit-logs/${row.id}`)}
              style={{ cursor: "pointer" }}
            >
              <Td>{row.occurred_at}</Td>
              <Td>{row.action}</Td>
              <Td>
                {row.target_type} · {row.target_id}
              </Td>
              <Td>{row.actor_user_id ?? "—"}</Td>
              <Td>{formatLabel(row.classification)}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </QueueShell>
  );
}

export function IntegrationEventsQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createSystemApi(client), [client]);
  const loadPage = useCallback(
    async ({
      cursor,
      status,
      sort,
      extra,
    }: {
      cursor?: string;
      status: string;
      sort: string;
      extra: Record<string, string>;
    }) => {
      const result = await api.listIntegrationEvents({
        ...(cursor ? { cursor } : {}),
        ...(status ? { status } : {}),
        ...(sort ? { sort } : {}),
        ...(extra.direction ? { direction: extra.direction } : {}),
        ...(extra.integration_type
          ? { integration_type: extra.integration_type }
          : {}),
        ...(extra.correlation_id
          ? { correlation_id: extra.correlation_id }
          : {}),
        limit: 25,
      });
      return { items: result.items, nextCursor: result.next_cursor ?? null };
    },
    [api],
  );
  const queue = useSystemQueue<IntegrationEventDto>(loadPage, {
    sort: "-occurred_at",
  });

  return (
    <QueueShell
      title="Integration events"
      description="Operational event history. Payload contents are not expanded — references only."
      breadcrumbs={[
        { label: "HUDDL HQ", to: "/app" },
        { label: "System", to: "/app/system" },
        { label: "Integration events" },
      ]}
      filters={
        <>
          <Input
            label="Status"
            value={queue.status}
            onChange={(e) => queue.setStatus(e.target.value)}
            placeholder="e.g. failed, completed"
          />
          <Input
            label="Integration type"
            value={queue.extra.integration_type ?? ""}
            onChange={(e) =>
              queue.setExtra("integration_type", e.target.value)
            }
          />
          <Input
            label="Direction"
            value={queue.extra.direction ?? ""}
            onChange={(e) => queue.setExtra("direction", e.target.value)}
          />
        </>
      }
      loading={queue.loading}
      error={queue.error}
      empty={queue.rows.length === 0}
      page={queue.page}
      nextCursor={queue.nextCursor}
      onRetry={() => void queue.load()}
      onPrev={queue.goPrevious}
      onNext={queue.goNext}
      onClear={queue.clearFilters}
    >
      <Table>
        <Thead>
          <Tr>
            <Th>Occurred</Th>
            <Th>Type</Th>
            <Th>Status</Th>
            <Th>Attempts</Th>
            <Th>Correlation</Th>
          </Tr>
        </Thead>
        <Tbody>
          {queue.rows.map((row) => (
            <Tr
              key={row.id}
              onClick={() =>
                navigate(`/app/system/integration-events/${row.id}`)
              }
              style={{ cursor: "pointer" }}
            >
              <Td>{row.occurred_at}</Td>
              <Td>
                {row.integration_type} · {row.direction}
              </Td>
              <Td>{row.status}</Td>
              <Td>{row.attempt_count}</Td>
              <Td>{row.correlation_id ?? "—"}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </QueueShell>
  );
}
